import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/api';
import { useNotification } from '../contexts/NotificationContext';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [createForm, setCreateForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'user',
    balance: 0,
    credits: 0
  });

  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    role: 'user',
    balance: 0,
    credits: 0,
    isActive: true
  });

  const [balanceForm, setBalanceForm] = useState({
    amount: 0,
    type: 'balance'
  });

  const { success, error } = useNotification();

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/admin/users', {
        params: {
          page: currentPage,
          search: searchTerm,
          role: roleFilter
        }
      });
      setUsers(response.data.users);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Failed to fetch users:', err);
      error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, error]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Validate password
      if (createForm.password.length < 6) {
        error('Password must be at least 6 characters long');
        return;
      }
      
      // Parse numeric values and prepare data
      const userData = {
        ...createForm,
        balance: parseFloat(createForm.balance) || 0,
        credits: parseInt(createForm.credits) || 0
      };

      console.log('Creating new user with data:', {
        ...userData,
        password: userData.password ? '********' : 'none' // Don't log the actual password
      });
      
      // Use the correct admin endpoint
      const response = await axios.post('/api/admin/users', userData);
      
      // Add the new user to the state
      setUsers([response.data.user, ...users]);
      setShowCreateModal(false);
      resetForm();
      success(`User ${userData.username} created successfully!`);
      
      // Refresh the user list to ensure we have the latest data
      fetchUsers();
    } catch (err) {
      console.error('User creation error:', err);
      error(err.response?.data?.message || 'Failed to create user');
    }
  };

  const resetForm = () => {
    setCreateForm({
      username: '',
      email: '',
      password: '',
      role: 'user',
      balance: 0,
      credits: 0
    });
    setEditForm({
      username: '',
      email: '',
      role: 'user',
      balance: 0,
      credits: 0,
      isActive: true
    });
    setBalanceForm({
      amount: 0,
      type: 'balance'
    });
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`/api/admin/users/${selectedUser._id}`, editForm);
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
      success('User updated successfully!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/admin/users/${selectedUser._id}/balance`, balanceForm);
      setShowBalanceModal(false);
      setSelectedUser(null);
      setBalanceForm({ amount: 0, type: 'balance' });
      fetchUsers();
      success(`${balanceForm.type} added successfully!`);
    } catch (err) {
      error(err.response?.data?.message || 'Failed to add balance');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await axios.delete(`/api/admin/users/${selectedUser._id}`);
      setShowDeleteModal(false);
      setSelectedUser(null);
      fetchUsers();
      success('User deleted successfully!');
    } catch (err) {
      error(err.response?.data?.message || 'Failed to delete user');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      role: user.role,
      balance: user.balance,
      credits: user.credits,
      isActive: user.isActive
    });
    setShowEditModal(true);
  };

  const openBalanceModal = (user) => {
    setSelectedUser(user);
    setBalanceForm({ amount: 0, type: 'balance' });
    setShowBalanceModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'owner':
        return 'text-red-400 bg-red-600/20';
      case 'reseller':
        return 'text-yellow-400 bg-yellow-600/20';
      case 'user':
        return 'text-green-400 bg-green-600/20';
      default:
        return 'text-gray-400 bg-gray-600/20';
    }
  };

  const UserCard = ({ user }) => (
    <div className="card-astral p-6 hover:glow-purple transition-all duration-300">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-bold text-white">{user.username}</h3>
          <p className="text-gray-400 text-sm">{user.email}</p>
        </div>
        <div className="flex flex-col items-end space-y-1">
          <span className={`px-2 py-1 text-xs rounded-full capitalize ${getRoleColor(user.role)}`}>
            {user.role}
          </span>
          <span className={`px-2 py-1 text-xs rounded-full ${user.isActive ? 'text-green-400 bg-green-600/20' : 'text-red-400 bg-red-600/20'}`}>
            {user.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center p-3 bg-green-600/10 rounded-lg">
          <div className="text-green-400 font-semibold">${user.balance}</div>
          <div className="text-xs text-gray-400">Balance</div>
        </div>
        <div className="text-center p-3 bg-blue-600/10 rounded-lg">
          <div className="text-blue-400 font-semibold">{user.credits}</div>
          <div className="text-xs text-gray-400">Credits</div>
        </div>
      </div>

      <div className="text-xs text-gray-500 mb-4">
        Joined: {new Date(user.createdAt).toLocaleDateString()}
        {user.lastLogin && (
          <span className="ml-2">
            Last login: {new Date(user.lastLogin).toLocaleDateString()}
          </span>
        )}
      </div>

      <div className="flex space-x-2">
        <button
          onClick={() => openEditModal(user)}
          className="flex-1 btn-neon-blue text-xs py-2"
        >
          Edit
        </button>
        <button
          onClick={() => openBalanceModal(user)}
          className="flex-1 btn-neon text-xs py-2"
        >
          Add Balance
        </button>
      </div>
      <div className="mt-2">
        <button
          onClick={() => openDeleteModal(user)}
          className="w-full btn-neon-red text-xs py-2 bg-red-600 hover:bg-red-700"
        
        >       

          Delete User
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <h2 className="text-2xl font-bold text-white">User Management</h2>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-neon"
        >
          Create New User
        </button>
      </div>

      {/* Filters */}
      <div className="card-astral p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Search Users
            </label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by username or email..."
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Filter by Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
            >
              <option value="">All Roles</option>
              <option value="user">User</option>
              <option value="reseller">Reseller</option>
              <option value="owner">Owner</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('');
                setCurrentPage(1);
              }}
              className="btn-neon-blue w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Users Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="card-astral p-8 text-center">
          <h3 className="text-xl font-bold text-gray-400 mb-2">No Users Found</h3>
          <p className="text-gray-500">No users match your current filters.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {users.map((user) => (
            <UserCard key={user._id} user={user} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="flex items-center px-4 py-2 text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn-neon-blue disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={createForm.username}
                  onChange={(e) => setCreateForm({...createForm, username: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(e) => setCreateForm({...createForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({...createForm, password: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                  minLength={6}
                />
                <p className="text-xs text-gray-400 mt-1">
                  Password must be at least 6 characters long
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({...createForm, role: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                >
                  <option value="user">User</option>
                  <option value="reseller">Reseller</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Initial Balance ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={createForm.balance}
                    onChange={(e) => setCreateForm({...createForm, balance: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Initial Credits
                  </label>
                  <input
                    type="number"
                    value={createForm.credits}
                    onChange={(e) => setCreateForm({...createForm, credits: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-neon flex-1">
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4 max-h-screen overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">Edit User</h2>
            <form onSubmit={handleEditUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Role
                </label>
                <select
                  value={editForm.role}
                  onChange={(e) => setEditForm({...editForm, role: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                >
                  <option value="user">User</option>
                  <option value="reseller">Reseller</option>
                  <option value="owner">Owner</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Balance ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={editForm.balance}
                    onChange={(e) => setEditForm({...editForm, balance: parseFloat(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Credits
                  </label>
                  <input
                    type="number"
                    value={editForm.credits}
                    onChange={(e) => setEditForm({...editForm, credits: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  />
                </div>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({...editForm, isActive: e.target.checked})}
                  className="mr-2"
                />
                <label htmlFor="isActive" className="text-sm text-gray-300">
                  Account Active
                </label>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-neon flex-1">
                  Update User
                </button>
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Balance Modal */}
      {showBalanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">
              Add {balanceForm.type === 'credits' ? 'Credits' : 'Balance'} to {selectedUser?.username}
            </h2>
            <form onSubmit={handleAddBalance} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Type
                </label>
                <select
                  value={balanceForm.type}
                  onChange={(e) => setBalanceForm({...balanceForm, type: e.target.value})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                >
                  <option value="balance">Balance ($)</option>
                  <option value="credits">Credits</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  step={balanceForm.type === 'balance' ? '0.01' : '1'}
                  value={balanceForm.amount}
                  onChange={(e) => setBalanceForm({...balanceForm, amount: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 bg-dark-200 border border-purple-500/30 rounded text-white"
                  required
                  min="0.01"
                />
              </div>
              <div className="bg-dark-200 p-3 rounded-lg">
                <div className="text-sm text-gray-400 mb-2">Current:</div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-green-400">${selectedUser?.balance}</span>
                    <div className="text-xs text-gray-500">Balance</div>
                  </div>
                  <div>
                    <span className="text-blue-400">{selectedUser?.credits}</span>
                    <div className="text-xs text-gray-500">Credits</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="btn-neon flex-1">
                  Add {balanceForm.type === 'credits' ? 'Credits' : 'Balance'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowBalanceModal(false)}
                  className="btn-neon-blue flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="card-astral p-6 w-full max-w-md mx-4">
            <h2 className="text-xl font-bold text-white mb-4">Delete User</h2>
            <p className="text-gray-300 mb-6">
              Are you sure you want to delete <span className="font-bold text-red-400">{selectedUser?.username}</span>? 
              This action cannot be undone.
            </p>
            <div className="flex gap-3 pt-4">
              <button 
                onClick={handleDeleteUser} 
                className="flex-1 bg-red-600 hover:bg-red-700 text-white text-xs py-2 px-3 rounded transition-colors"
                >
                Delete User
                
              </button>
              
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="btn-neon-blue flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;